import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Table, TableBody,
  TableCell, TableHead, TablePagination, TableRow, TextField, Typography,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import { cloError, cloGet, cloPost, ENDPOINTS } from "./api";
import CandidateSearch, { Candidate } from "./CandidateSearch";
import MerchantDetail from "./MerchantDetail";

const MerchantsTab: React.FC = () => {
  const notify = useNotify();
  const [rows, setRows] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", categoryId: "" });
  const [sources, setSources] = useState<Candidate[]>([]);

  const load = useCallback(async () => {
    try {
      const res = await cloGet(ENDPOINTS.CLO_MERCHANTS, { page: page + 1, pageSize, search: search || undefined, status: status || undefined });
      setRows(res.data || []);
      setTotal(res.pagination?.total || 0);
      setCategories(res.metadata?.categories || []);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [page, pageSize, search, status, notify]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    setForm({ name: "", categoryId: "" });
    setSources([]);
    setCreating(true);
  };

  const pick = (c: Candidate) => {
    setSources([...sources, c]);
    if (!form.name) setForm({ ...form, name: c.name });
  };

  const create = async () => {
    try {
      const res = await cloPost(ENDPOINTS.CLO_MERCHANTS, {
        name: form.name,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        brandIds: sources.map((s) => s.id),
      });
      notify("Merchant added; add its bank text patterns next", "info");
      setCreating(false);
      load();
      setOpenId(res.data.id);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  return (
    <Paper>
      <Box p={2} display="flex" alignItems="center" style={{ gap: 16 }}>
        <TextField label="Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
        <TextField select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} style={{ minWidth: 140 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">active</MenuItem>
          <MenuItem value="disabled">disabled</MenuItem>
          <MenuItem value="merged">merged</MenuItem>
        </TextField>
        <Box flexGrow={1} />
        <Button color="primary" variant="contained" onClick={openCreate}>Add merchant</Button>
      </Box>
      <Box px={2}>
        <Typography variant="body2" color="textSecondary">
          Add the CLO retailers and the competitors each campaign needs. Bank transactions are matched to a merchant by its patterns.
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>CLO retailer</TableCell>
            <TableCell>Sources</TableCell>
            <TableCell>Patterns</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{r.name}</TableCell>
              <TableCell>{r.category_name || "—"}</TableCell>
              <TableCell>{r.partner_name ? <Chip size="small" color="primary" label={r.partner_name} /> : "—"}</TableCell>
              <TableCell>{r.source_count}</TableCell>
              <TableCell>{Number(r.pattern_count) === 0 ? <Chip size="small" label="none" /> : r.pattern_count}</TableCell>
              <TableCell>{r.status}</TableCell>
              <TableCell align="right"><Button size="small" color="primary" onClick={() => setOpenId(r.id)}>Open</Button></TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={7} align="center">No merchants</TableCell></TableRow>}
        </TableBody>
      </Table>
      <TablePagination
        component="div" count={total} page={page} rowsPerPage={pageSize}
        onChangePage={(_, p) => setPage(p)}
        onChangeRowsPerPage={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
      />

      <Dialog disableBackdropClick open={creating} onClose={() => setCreating(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add merchant</DialogTitle>
        <DialogContent>
          <CandidateSearch picked={sources.map((s) => s.id)} onPick={pick} />
          {sources.length > 0 && (
            <Box my={1}>
              {sources.map((s) => (
                <Chip key={s.id} size="small" label={s.name} style={{ margin: 2 }}
                  onDelete={() => setSources(sources.filter((x) => x.id !== s.id))} />
              ))}
            </Box>
          )}
          <TextField fullWidth margin="dense" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            helperText="Not in either list (e.g. Aldi)? Just type the name." />
          <TextField select fullWidth margin="dense" label="Category" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
            <MenuItem value="">None</MenuItem>
            {categories.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.display_name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button color="primary" variant="contained" disabled={!form.name.trim()} onClick={create}>Add</Button>
        </DialogActions>
      </Dialog>

      <MerchantDetail cloMerchantId={openId} categories={categories} onClose={() => setOpenId(null)} onChanged={load} />
    </Paper>
  );
};

export default MerchantsTab;
